// src/App.js
import { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import ReviewCard from './components/ReviewCard';
import ImplantsList from './components/ImplantsList';
import AddImplant from './components/AddImplant';
import StatusBadge from './components/StatusBadge';

const TABS = ['queue', 'database', 'add'];

export default function App() {
  const [tab, setTab]         = useState('queue');
  const [counts, setCounts]   = useState({});
  const [health, setHealth]   = useState(null);
  const [queue, setQueue]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [toast, setToast]     = useState(null);
  const [schema, setSchema]   = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadHealth = useCallback(async () => {
    try {
      const h = await api.health();
      setHealth(h);
    } catch (e) {
      setHealth(null);
    }
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const c = await api.getCounts();
      setCounts(c);
    } catch (e) {}
  }, []);

  const loadSchema = useCallback(async () => {
    try {
      const s = await api.getSchema();
      setSchema(s.taxonomy);
    } catch (e) {}
  }, []);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getQueue('needs_review');
      setQueue(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    loadCounts();
    loadSchema();
    loadQueue();
    const interval = setInterval(() => { loadCounts(); loadHealth(); }, 30000);
    return () => clearInterval(interval);
  }, [loadHealth, loadCounts, loadSchema, loadQueue]);

  const handleApprove = async (kNumber, body) => {
    await api.approve(kNumber, body);
    showToast(`✓ ${body.implant_name} added to database`);
    setQueue(q => q.filter(i => i.k_number !== kNumber));
    loadCounts();
  };

  const handleReject = async (kNumber) => {
    await api.reject(kNumber);
    showToast('Implant rejected', 'info');
    setQueue(q => q.filter(i => i.k_number !== kNumber));
    loadCounts();
  };

  return (
    <div style={s.root}>
      {/* Sidebar */}
      <aside style={s.sidebar}>
        <div style={s.brand}>
          <span style={s.brandIcon}>🦷</span>
          <div>
            <div style={s.brandName}>AI Dentist</div>
            <div style={s.brandSub}>Implant Admin</div>
          </div>
        </div>

        {/* Health indicator */}
        <div style={s.healthBox}>
          <div style={{ ...s.healthDot, background: health ? '#22c55e' : '#ef4444' }} />
          <div>
            <div style={s.healthLabel}>API Status</div>
            <div style={s.healthVal}>
              {health ? `${health.implants_in_db} implants` : 'Offline'}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={s.nav}>
          {[
            { key: 'queue',    label: 'Review Queue',  icon: '📋', count: counts.needs_review },
            { key: 'database', label: 'Live Database', icon: '🗄️',  count: health?.implants_in_db },
            { key: 'add',      label: 'Add Manually',  icon: '➕' },
          ].map(item => (
            <button
              key={item.key}
              style={{ ...s.navItem, ...(tab === item.key ? s.navItemActive : {}) }}
              onClick={() => setTab(item.key)}
            >
              <span style={s.navIcon}>{item.icon}</span>
              <span style={s.navLabel}>{item.label}</span>
              {item.count > 0 && (
                <span style={{ ...s.navBadge, ...(item.key === 'queue' ? s.navBadgeAlert : {}) }}>
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Status counts */}
        <div style={s.countsBox}>
          <div style={s.countsTitle}>Pipeline Status</div>
          {[
            { key: 'new',                   label: 'Discovered',    color: '#60a5fa' },
            { key: 'extracting',            label: 'Extracting PDF',color: '#a78bfa' },
            { key: 'classifying',           label: 'Classifying',   color: '#f59e0b' },
            { key: 'needs_review',          label: 'Needs Review',  color: '#f97316' },
            { key: 'approved',              label: 'Approved',      color: '#22c55e' },
            { key: 'extraction_failed',     label: 'Extract Failed',color: '#ef4444' },
            { key: 'classification_failed', label: 'Class. Failed', color: '#ef4444' },
          ].map(({ key, label, color }) => counts[key] > 0 && (
            <div key={key} style={s.countRow}>
              <div style={{ ...s.countDot, background: color }} />
              <span style={s.countLabel}>{label}</span>
              <span style={s.countNum}>{counts[key]}</span>
            </div>
          ))}
        </div>
      </aside>

      {/* Main content */}
      <main style={s.main}>
        {/* Toast */}
        {toast && (
          <div style={{ ...s.toast, ...(toast.type === 'info' ? s.toastInfo : s.toastSuccess) }}>
            {toast.msg}
          </div>
        )}

        {tab === 'queue' && (
          <div>
            <div style={s.pageHeader}>
              <div>
                <h1 style={s.pageTitle}>Review Queue</h1>
                <p style={s.pageSubtitle}>
                  {queue.length} implant{queue.length !== 1 ? 's' : ''} awaiting your review.
                  Verify GPT-4o's feature suggestions and approve or reject.
                </p>
              </div>
              <button style={s.refreshBtn} onClick={loadQueue}>↻ Refresh</button>
            </div>

            {loading && <div style={s.emptyState}>Loading queue...</div>}
            {error   && <div style={s.errorState}>Error: {error}</div>}
            {!loading && !error && queue.length === 0 && (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>✓</div>
                <div style={s.emptyText}>Queue is clear</div>
                <div style={s.emptySub}>All pending implants have been reviewed.</div>
              </div>
            )}
            {!loading && queue.map(item => (
              <ReviewCard
                key={item.k_number}
                item={item}
                schema={schema}
                onApprove={handleApprove}
                onReject={handleReject}
                onError={(msg) => showToast(msg, 'error')}
              />
            ))}
          </div>
        )}

        {tab === 'database' && (
          <ImplantsList
            onError={(msg) => showToast(msg, 'error')}
            onSuccess={(msg) => showToast(msg)}
          />
        )}

        {tab === 'add' && (
          <AddImplant
            schema={schema}
            onSuccess={(msg) => { showToast(msg); loadCounts(); loadHealth(); }}
            onError={(msg) => showToast(msg, 'error')}
          />
        )}
      </main>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────
const s = {
  root: {
    display: 'flex', minHeight: '100vh',
    fontFamily: "'Inter', sans-serif",
    background: '#0f1117', color: '#e2e8f0',
  },
  sidebar: {
    width: 240, minHeight: '100vh', background: '#0a0d14',
    borderRight: '1px solid #1e2530',
    display: 'flex', flexDirection: 'column',
    padding: '0 0 24px 0', position: 'sticky', top: 0,
    flexShrink: 0,
  },
  brand: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '24px 20px 20px', borderBottom: '1px solid #1e2530',
  },
  brandIcon: { fontSize: 28 },
  brandName: { fontSize: 15, fontWeight: 700, color: '#f1f5f9', letterSpacing: '-0.02em' },
  brandSub:  { fontSize: 11, color: '#64748b', fontFamily: "'IBM Plex Mono', monospace" },
  healthBox: {
    display: 'flex', alignItems: 'center', gap: 10,
    margin: '16px 16px 8px', padding: '10px 12px',
    background: '#111827', borderRadius: 8, border: '1px solid #1e2530',
  },
  healthDot:   { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  healthLabel: { fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  healthVal:   { fontSize: 13, color: '#94a3b8', fontFamily: "'IBM Plex Mono', monospace" },
  nav: { padding: '8px 12px', flex: 1 },
  navItem: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
    background: 'transparent', color: '#64748b', fontSize: 13, fontWeight: 500,
    marginBottom: 2, transition: 'all 0.15s',
    fontFamily: "'Inter', sans-serif",
  },
  navItemActive: { background: '#1e2d45', color: '#60a5fa' },
  navIcon:  { fontSize: 16, width: 20, textAlign: 'center' },
  navLabel: { flex: 1, textAlign: 'left' },
  navBadge: {
    fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
    background: '#1e2530', color: '#94a3b8', fontFamily: "'IBM Plex Mono', monospace",
  },
  navBadgeAlert: { background: '#7c2d12', color: '#fb923c' },
  countsBox: {
    margin: '0 16px', padding: '12px 14px',
    background: '#111827', borderRadius: 8, border: '1px solid #1e2530',
  },
  countsTitle: { fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 },
  countRow:   { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 },
  countDot:   { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  countLabel: { flex: 1, fontSize: 11, color: '#64748b' },
  countNum:   { fontSize: 11, color: '#94a3b8', fontFamily: "'IBM Plex Mono', monospace" },
  main: { flex: 1, padding: '32px 40px', maxWidth: 960, position: 'relative' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  pageTitle:  { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0, letterSpacing: '-0.03em' },
  pageSubtitle: { fontSize: 13, color: '#64748b', marginTop: 4 },
  refreshBtn: {
    padding: '8px 16px', background: '#1e2530', border: '1px solid #2d3748',
    borderRadius: 7, color: '#94a3b8', cursor: 'pointer', fontSize: 13,
    fontFamily: "'Inter', sans-serif",
  },
  emptyState: { textAlign: 'center', padding: '80px 0', color: '#475569' },
  emptyIcon:  { fontSize: 48, marginBottom: 12 },
  emptyText:  { fontSize: 18, fontWeight: 600, color: '#64748b' },
  emptySub:   { fontSize: 13, marginTop: 4 },
  errorState: { padding: 20, background: '#1c0a0a', border: '1px solid #7f1d1d', borderRadius: 8, color: '#fca5a5' },
  toast: {
    position: 'fixed', top: 20, right: 20, padding: '12px 20px',
    borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 9999,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)', animation: 'none',
  },
  toastSuccess: { background: '#14532d', border: '1px solid #166534', color: '#86efac' },
  toastInfo:    { background: '#1e3a5f', border: '1px solid #1d4ed8', color: '#93c5fd' },
};
